import { IStrapiRichText } from '@components/common/StrapiRichText/StrapiRichText.type';
import { IPartsInQuest } from '@devHub/interfaces/devhub';
import { IPossibility, IQuestion, IQuiz, IQuizWithAnswers, IUserQuizAnswers } from '@devHub/interfaces/quiz';
import {
	EQuestPartsType,
	EQuestType
} from '@devHubSingleQuest/components/DevHubSingleQuestPage/DevHubSingleQuestPage.enum';
import { IUserResults } from '@devHubSingleQuest/components/Parts/Parts.type';
import { EQuizQuestionType } from '@devHubSingleQuest/components/QuizCard/QuizCard.enum';
import { removeLocalStorageWithKeyStartingBy } from '@functions/localStorage';
import { EPromptType } from '@shared/cli/components/Prompt/Prompt.enum';
import { addPrompt, addPromptIfNotAlreadyIn } from '@shared/cli/components/Prompt/Prompt.functions';
import { slugQueryAtom } from '@store/appRouter/appRouter.atoms';
import { atom } from 'jotai';
import { atomFamily, atomWithStorage } from 'jotai/utils';
import { EQuestContextualModalOrigin } from './devhubSingleQuestStore.enum';
import {
	ICurrentQuestIndex,
	IHintsButtonState,
	INewQuestPrompt,
	INewQuestPromptWithUniqueKey,
	INextButtonState,
	IQuestPrompt,
	IVerifyButtonState
} from './devhubSingleQuestStore.types';
import {
	CURRENT_QUEST_INDEX_KEY,
	QUEST_PROMPT_KEY,
	defaultCurrentQuestIndex,
	defaultHintsButtonState,
	defaultNextButtonState,
	defaultVerifyButtonState
} from './devhubSingleQuestStore.variables';

export const isValidatingPartAtom = atom<boolean>(false);

export const questPartControlTypeAtom = atom<EQuestPartsType>(EQuestPartsType.start);
export const setQuestPartControlTypeAtom = atom(null, (get, set, newType: EQuestPartsType) => {
	set(questPartControlTypeAtom, newType);
});

const questCurrentSlugAndIndexAtom = atomWithStorage<ICurrentQuestIndex>(
	CURRENT_QUEST_INDEX_KEY,
	defaultCurrentQuestIndex
);

export const questCurrentIndexAtom = atom(
	(get) => {
		const { quest } = get(questCurrentSlugAndIndexAtom);
		const slug = get(slugQueryAtom);

		// i.e: user closes the tab and re-opens on a different quest
		if (quest !== slug) return 0;

		return get(questCurrentSlugAndIndexAtom).index;
	},
	(get, set, newIndex: number) => {
		const quest = get(slugQueryAtom);
		set(questCurrentSlugAndIndexAtom, { quest, index: newIndex });
	}
);

export const userResultsAtom = atom<IUserResults | undefined>(undefined);

export const validatedPartIndexesAtom = atom<number[]>([]);

export const userHasValidatedTheQuestAtom = atom<boolean>(false);

export const questContextualModalStateAtom = atom<{
	type: EQuestType;
	isOpen: boolean;
	origin?: EQuestContextualModalOrigin;
}>({ type: EQuestType.unset, isOpen: false });
export const isQuestContextualModalOpenAtom = atom<boolean>((get) => {
	const questContextualModalState = get(questContextualModalStateAtom);
	if (!questContextualModalState) return false;

	return questContextualModalState.isOpen;
});
export const setIsQuestContextualModalOpenAtom = atom(
	null,
	(get, set, newState: { type: EQuestType; isOpen: boolean; origin?: EQuestContextualModalOrigin } | false) => {
		if (!newState) {
			const prevQuestContextualModalState = get(questContextualModalStateAtom);
			set(questContextualModalStateAtom, { ...prevQuestContextualModalState, isOpen: false });
			return;
		}

		const { type, isOpen, origin } = newState;
		set(questContextualModalStateAtom, { type, isOpen, origin });
	}
);

export const isQuestContextualModalOpenFromVerifyAtom = atom<boolean>((get) => {
	const questContextualModalState = get(questContextualModalStateAtom);

	if (
		questContextualModalState?.type !== EQuestType.ctf ||
		questContextualModalState.origin !== EQuestContextualModalOrigin.verify
	)
		return false;

	return questContextualModalState.isOpen;
});

export const isQuestContextualModalOpenFromDeployAtom = atom<boolean>((get) => {
	const questContextualModalState = get(questContextualModalStateAtom);

	if (
		questContextualModalState?.type !== EQuestType.ctf ||
		questContextualModalState.origin !== EQuestContextualModalOrigin.deploy
	)
		return false;

	return questContextualModalState.isOpen;
});

export const isCTFWalletLinkedAtom = atom<boolean>(false);

// Create an atom family for the quests prompts
export const questPromptAtomFamily = atomFamily((questSlug: string) =>
	atomWithStorage<IQuestPrompt[]>(`${QUEST_PROMPT_KEY}_${questSlug}`, [])
);

export const addQuestPromptAtom = atom(null, (get, set, newPrompt: INewQuestPrompt) => {
	const slug = get(slugQueryAtom);

	if (!slug) return;

	const questPromptAtom = questPromptAtomFamily(slug);
	set(questPromptAtom, addPrompt(get(questPromptAtom), newPrompt));
});

export const addQuestPromptIfNotAlreadyInAtom = atom(null, (get, set, newPrompt: INewQuestPrompt) => {
	const slug = get(slugQueryAtom);

	if (!slug) return;

	const questPromptAtom = questPromptAtomFamily(slug);
	set(questPromptAtom, addPromptIfNotAlreadyIn(get(questPromptAtom), newPrompt, 'message'));
});

export const addQuestPromptIfNotAlreadyInWithKeyAtom = atom(
	null,
	(get, set, newPromptWithKey: INewQuestPromptWithUniqueKey) => {
		const slug = get(slugQueryAtom);

		if (!slug) return;

		const { newPrompt, uniqueKey } = newPromptWithKey;

		if (!uniqueKey || !newPrompt) return;

		const questPromptAtom = questPromptAtomFamily(slug);
		set(questPromptAtom, addPromptIfNotAlreadyIn(get(questPromptAtom), newPrompt, uniqueKey));
	}
);

export const isQuestPromptLoadingAtom = atom<boolean>((get) => {
	const slug = get(slugQueryAtom);

	if (!slug) return false;

	const prompts = get(questPromptAtomFamily(slug));

	if (!prompts) return false;

	// check if last prompt is loading
	return prompts[prompts.length - 1]?.type === EPromptType.loading;
});

// the verify button in the single quest page
export const verifyButtonStateAtom = atom(defaultVerifyButtonState);
export const setVerifyButtonStateAtom = atom(null, (get, set, newState: Partial<IVerifyButtonState>) => {
	set(verifyButtonStateAtom, { ...get(verifyButtonStateAtom), ...newState });
});

// the hints button in the single quest page
export const hintsButtonStateAtom = atom(defaultHintsButtonState);
export const setHintsButtonStateAtom = atom(null, (get, set, newState: Partial<IHintsButtonState>) => {
	set(hintsButtonStateAtom, { ...get(hintsButtonStateAtom), ...newState });
});

// the next step button in the single quest page
export const nextButtonStateAtom = atom(defaultNextButtonState);
export const setNextButtonStateAtom = atom(null, (get, set, newState: Partial<INextButtonState>) => {
	set(nextButtonStateAtom, { ...get(nextButtonStateAtom), ...newState });
});

// hints modal visibility
export const isHintsModalOpenAtom = atom<boolean>(false);
export const setIsHintsModalOpenAtom = atom(null, (get, set, isOpen: boolean) => {
	set(isHintsModalOpenAtom, isOpen);
});
/*
 * QUIZ
 */
export const quizAtom = atom<IQuiz | undefined>(undefined);
export const setQuizAtom = atom(null, (get, set, payload: IQuiz | undefined) => {
	set(quizAtom, payload);
});
export const updateCorrectQuestionsAtom = atom(null, (get, set, payload: IQuestion['id'][]) => {
	const quiz = get(quizAtom);

	if (!quiz?.questions) {
		return;
	}

	let newQuiz = { ...quiz };

	newQuiz?.questions?.forEach((question: IQuestion) => {
		question.isCorrect = payload.some((id: number) => id === question.id);
	});

	set(quizAtom, newQuiz);
});
export const setQuizIsSuccessAtom = atom(null, (get, set) => {
	const quiz = get(quizAtom);

	if (!quiz) {
		return;
	}

	const newQuiz = { ...quiz, quizSuccess: true };
	// set all questions isCorrect to true
	newQuiz?.questions?.forEach((question) => {
		question.isCorrect = true;
	});

	set(quizAtom, newQuiz);
});

export const refreshQuizAtom = atom(null, (get, set) => {
	const quiz = get(quizAtom);
	quiz?.questions?.forEach((question) => {
		if (!question.isCorrect) {
			set(resetQuestionAnswersAtom, question.id);
		}
	});

	set(quizAtom, { ...quiz, questions: quiz?.nextQuestions, nextQuestions: undefined });
});

export const initQuizFromQuizWithAnswersAtom = atom(null, (get, set, payload: IQuizWithAnswers) => {
	try {
		if (!payload) {
			return undefined;
		}

		const res = { ...payload };

		delete res.answers;

		set(quizAtom, res);
	} catch {
		set(quizAtom, undefined);
	}
});

// Create a base atom for the user quiz answers state
export const userQuizAnswersAtom = atom({} as IUserQuizAnswers);

// Define atoms for your action creators
export const initQuestQuizAtom = atom(null, (get, set, payload: IUserQuizAnswers) => {
	set(userQuizAnswersAtom, payload);
});

export const updatePossibilityForQuestionAtom = atom(
	null,
	(
		get,
		set,
		payload: { questionId: number; answerIndex: IPossibility['answerIndex']; questionType: IQuestion['type'] }
	) => {
		const { questionId, answerIndex, questionType } = payload;
		const state = get(userQuizAnswersAtom);

		if (state[questionId]) {
			if (questionType === EQuizQuestionType.radio) {
				state[questionId] = [answerIndex];
			} else {
				const answerIndexIndex = state[questionId].indexOf(answerIndex);
				if (answerIndexIndex !== -1) {
					state[questionId].splice(answerIndexIndex, 1);
				} else {
					state[questionId].push(answerIndex);
				}
			}
		} else {
			state[questionId] = [answerIndex];
		}

		set(userQuizAnswersAtom, { ...state });
	}
);

export const resetQuestionAnswersAtom = atom(null, (get, set, questionId: number) => {
	const state = get(userQuizAnswersAtom);
	state[questionId] = [];
	set(userQuizAnswersAtom, { ...state });
});

export const resetUserQuizAnswersAtom = atom(null, (get, set) => {
	set(userQuizAnswersAtom, {} as IUserQuizAnswers);
});

// Create a base atom for the last submit click timestamp state
export const quizRateLimitDataAtom = atom({ startTimestamp: undefined, clickCount: 0 });
// update clickCount
export const incrementQuizRateLimitClickCountAtom = atom(null, (get, set) => {
	const state = get(quizRateLimitDataAtom);
	set(quizRateLimitDataAtom, { ...state, clickCount: state.clickCount + 1 });
});

/*
 * QUEST PART
 */

// Create atom for formattedParts state
export const questPartsAtom = atom<IPartsInQuest[]>([]);

// Create atom for initializing parts
export const initQuestPartsAtom = atom(null, (get, set, payload: IPartsInQuest[]) => {
	const currentParts = get(questPartsAtom);

	if (currentParts.length > 0) return;

	set(questPartsAtom, payload);
});

// Create atom for validating a part
export const validateQuestPartAtom = atom(null, (get, set, validatedPartIndex: number) => {
	const currentParts = get(questPartsAtom);
	const newParts = currentParts.map((part, index) =>
		index === validatedPartIndex ? { ...part, isCompleted: true } : part
	);
	set(questPartsAtom, newParts);
});

// Create atom for adding a hint
export const addHintToQuestPartAtom = atom(
	null,
	(get, set, payload: { partIndex: number; hint: IStrapiRichText[] }) => {
		const currentParts = get(questPartsAtom);
		const { partIndex, hint } = payload;
		const newParts = currentParts.map((part, index) => {
			if (index === partIndex) {
				const newHints = part.hints ? [...part.hints] : [];
				const isHintAlreadyInArray = newHints.some(
					(hintInArray: any) => JSON.stringify(hintInArray) === JSON.stringify(hint)
				);
				if (!isHintAlreadyInArray) {
					newHints.push(hint);
				}
				return { ...part, hints: newHints };
			}
			return part;
		});
		set(questPartsAtom, newParts);
	}
);

export const resetQuestStoreAtom = atom(null, (get, set) => {
	set(questPartsAtom, []);
	set(quizAtom, {});
	set(userQuizAnswersAtom, {} as IUserQuizAnswers);
	set(quizRateLimitDataAtom, { startTimestamp: undefined, clickCount: 0 });
	set(verifyButtonStateAtom, { ...defaultVerifyButtonState });
	set(hintsButtonStateAtom, { ...defaultHintsButtonState });
	set(nextButtonStateAtom, { ...defaultNextButtonState });
	set(isHintsModalOpenAtom, false);
	set(isValidatingPartAtom, false);
	set(validatedPartIndexesAtom, []);
	set(questContextualModalStateAtom, { type: EQuestType.unset, isOpen: false });
	set(isCTFWalletLinkedAtom, false);
	set(userHasValidatedTheQuestAtom, false);
	set(questCurrentIndexAtom, 0);
	set(questPartControlTypeAtom, EQuestPartsType.start);

	// remove all atoms from questPromptAtomFamily
	questPromptAtomFamily.setShouldRemove(() => true);
	// clear the remove function
	questPromptAtomFamily.setShouldRemove(null);
	// clean up local storage
	removeLocalStorageWithKeyStartingBy(QUEST_PROMPT_KEY);
});
